import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Trash2, Pencil, Save, X, Users, ClipboardList, CalendarDays, BarChart3, Check, Copy, RefreshCw } from "lucide-react";
import { toast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  { key: "cucina", label: "Cucina", color: "#7A1F1A" },
  { key: "sala", label: "Sala", color: "#4a1c17" },
  { key: "piani", label: "Piani", color: "#8B5A2B" },
  { key: "reception", label: "Reception", color: "#C48B31" },
  { key: "manutenzione", label: "Manutenzione", color: "#5A5A5A" },
  { key: "altro", label: "Altro", color: "#666" },
];
const SLOTS = [
  { key: "mattina", label: "Mattina" },
  { key: "pranzo", label: "Pranzo" },
  { key: "pomeriggio", label: "Pomeriggio" },
  { key: "sera", label: "Sera" },
  { key: "sempre", label: "Sempre" },
];
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const FREQ = [
  { key: "daily", label: "Ogni giorno" },
  { key: "weekly", label: "Settimanale" },
  { key: "monthly", label: "Mensile" },
  { key: "on_demand", label: "Su richiesta" },
];

function areaMeta(k) { return AREAS.find(a => a.key === k) || AREAS[AREAS.length - 1]; }

function authHeaders() {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function Chip({ active, onClick, children, color = "#4a1c17" }) {
  return (
    <button
      onClick={onClick}
      data-testid={`staff-chip-${String(children).toLowerCase()}`}
      className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${active ? "text-white" : "bg-white text-[#4a1c17] border border-[#e6d3c0]"}`}
      style={active ? { backgroundColor: color } : {}}
    >{children}</button>
  );
}

// ------------------- MEMBERS TAB -------------------

function MembersTab({ members, tasks, onReload }) {
  const [editing, setEditing] = useState(null); // member or new one

  const emptyMember = () => ({
    name: "", role: "", area: "cucina", color: "#4a1c17",
    rest_days: [1], phone: "", notes: "", active: true,
    weekly_schedule: Object.fromEntries([...Array(7)].map((_, i) => [String(i), i === 1 ? { off: true } : { start: "09:00", end: "17:00" }])),
  });

  async function save(m) {
    try {
      if (m.id) {
        await axios.put(`${API}/admin/staff/members/${m.id}`, m, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/staff/members`, m, { headers: authHeaders() });
      }
      setEditing(null);
      onReload();
      toast({ title: "Team aggiornato" });
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    }
  }
  async function remove(id) {
    if (!window.confirm("Rimuovere questo membro dal team?")) return;
    await axios.delete(`${API}/admin/staff/members/${id}`, { headers: authHeaders() });
    onReload();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#4a1c17]">Team ({members.length})</h3>
          <p className="text-sm text-neutral-600">Anagrafica, ruoli e orari settimanali</p>
        </div>
        <button data-testid="staff-add-member-btn" onClick={() => setEditing(emptyMember())} className="px-3 py-2 bg-[#4a1c17] text-white rounded-lg text-sm font-medium flex items-center gap-1">
          <Plus className="h-4 w-4" /> Aggiungi
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {members.map(m => (
          <div key={m.id} className="bg-white rounded-xl p-4 border border-[#e6d3c0] flex gap-3">
            <div className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" style={{ backgroundColor: m.color || "#4a1c17" }}>
              {m.initials || (m.name?.[0] || "?")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-bold text-[#4a1c17]">{m.name}</div>
                <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: areaMeta(m.area).color }}>{areaMeta(m.area).label}</span>
              </div>
              <div className="text-sm text-neutral-700 mt-0.5">{m.role}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {DAYS.map((d, i) => {
                  const slot = m.weekly_schedule?.[String(i)] || {};
                  const off = slot.off || (m.rest_days || []).includes(i);
                  return (
                    <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${off ? "bg-neutral-200 text-neutral-500 line-through" : "bg-[#F7EBDF] text-[#4a1c17]"}`}>
                      {d}{off ? "" : ` ${slot.start || "?"}-${slot.end || "?"}`}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <button data-testid={`staff-edit-${m.id}`} onClick={() => setEditing(m)} className="p-1.5 text-neutral-600 hover:text-[#4a1c17]"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`staff-del-${m.id}`} onClick={() => remove(m.id)} className="p-1.5 text-neutral-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && <MemberEditor member={editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function MemberEditor({ member, onSave, onCancel }) {
  const [m, setM] = useState({ ...member });

  function setDay(i, patch) {
    const cur = m.weekly_schedule || {};
    const day = { ...(cur[String(i)] || {}) };
    Object.assign(day, patch);
    setM({ ...m, weekly_schedule: { ...cur, [String(i)]: day } });
  }
  function toggleRest(i) {
    const rd = new Set(m.rest_days || []);
    rd.has(i) ? rd.delete(i) : rd.add(i);
    const day = { off: rd.has(i) };
    if (!rd.has(i) && !m.weekly_schedule?.[String(i)]?.start) {
      day.start = "09:00"; day.end = "17:00"; day.off = false;
    }
    setM({ ...m, rest_days: [...rd].sort(), weekly_schedule: { ...m.weekly_schedule, [String(i)]: day } });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#e6d3c0] p-4 flex items-center justify-between">
          <h3 className="font-bold text-[#4a1c17] text-lg">{m.id ? "Modifica membro" : "Nuovo membro"}</h3>
          <button onClick={onCancel} data-testid="staff-editor-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">Nome
              <input data-testid="staff-name-input" value={m.name} onChange={e => setM({ ...m, name: e.target.value })} className="w-full border rounded px-2 py-1.5 mt-1" />
            </label>
            <label className="text-sm">Area
              <select data-testid="staff-area-select" value={m.area} onChange={e => setM({ ...m, area: e.target.value, color: areaMeta(e.target.value).color })} className="w-full border rounded px-2 py-1.5 mt-1">
                {AREAS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </label>
          </div>
          <label className="text-sm block">Ruolo
            <input data-testid="staff-role-input" value={m.role} onChange={e => setM({ ...m, role: e.target.value })} placeholder="es. Chef, Comande, Governante" className="w-full border rounded px-2 py-1.5 mt-1" />
          </label>
          <label className="text-sm block">Telefono
            <input value={m.phone || ""} onChange={e => setM({ ...m, phone: e.target.value })} className="w-full border rounded px-2 py-1.5 mt-1" />
          </label>

          <div>
            <div className="text-sm font-medium text-[#4a1c17] mb-2">Orari settimanali</div>
            <div className="space-y-1.5">
              {DAYS.map((d, i) => {
                const slot = m.weekly_schedule?.[String(i)] || {};
                const isRest = (m.rest_days || []).includes(i) || slot.off;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-10 text-sm font-medium">{d}</div>
                    <button onClick={() => toggleRest(i)} className={`px-2 py-0.5 rounded text-xs ${isRest ? "bg-neutral-300" : "bg-[#F7EBDF] text-[#4a1c17]"}`}>{isRest ? "Riposo" : "Lavora"}</button>
                    {!isRest && <>
                      <input type="time" value={slot.start || ""} onChange={e => setDay(i, { start: e.target.value, off: false })} className="border rounded px-1.5 py-0.5 text-sm" />
                      <span>–</span>
                      <input type="time" value={slot.end || ""} onChange={e => setDay(i, { end: e.target.value, off: false })} className="border rounded px-1.5 py-0.5 text-sm" />
                    </>}
                  </div>
                );
              })}
            </div>
          </div>

          <label className="text-sm block">Note
            <textarea value={m.notes || ""} onChange={e => setM({ ...m, notes: e.target.value })} rows={2} className="w-full border rounded px-2 py-1.5 mt-1" />
          </label>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#e6d3c0] p-3 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 border rounded-lg text-sm">Annulla</button>
          <button data-testid="staff-save-member" onClick={() => onSave(m)} className="px-3 py-2 bg-[#4a1c17] text-white rounded-lg text-sm flex items-center gap-1">
            <Save className="h-4 w-4" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------- TASKS TAB -------------------

function TasksTab({ tasks, members, onReload }) {
  const [editing, setEditing] = useState(null);
  const [filterArea, setFilterArea] = useState("all");

  const filtered = filterArea === "all" ? tasks : tasks.filter(t => t.area === filterArea);

  const empty = () => ({ title: "", description: "", area: "cucina", frequency: "daily", days_of_week: [], slot: "sempre", active: true, assigned_member_id: null });

  async function save(t) {
    try {
      if (t.id) await axios.put(`${API}/admin/staff/tasks/${t.id}`, t, { headers: authHeaders() });
      else await axios.post(`${API}/admin/staff/tasks`, t, { headers: authHeaders() });
      setEditing(null); onReload(); toast({ title: "Attività salvata" });
    } catch (e) { toast({ title: "Errore", description: e.message, variant: "destructive" }); }
  }
  async function remove(id) {
    if (!window.confirm("Eliminare questa attività?")) return;
    await axios.delete(`${API}/admin/staff/tasks/${id}`, { headers: authHeaders() });
    onReload();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-[#4a1c17]">Attività ricorrenti ({tasks.length})</h3>
          <p className="text-sm text-neutral-600">Template attività assegnate per ruolo o persona</p>
        </div>
        <button data-testid="staff-add-task-btn" onClick={() => setEditing(empty())} className="px-3 py-2 bg-[#4a1c17] text-white rounded-lg text-sm font-medium flex items-center gap-1">
          <Plus className="h-4 w-4" /> Aggiungi
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip active={filterArea === "all"} onClick={() => setFilterArea("all")}>Tutte</Chip>
        {AREAS.map(a => <Chip key={a.key} active={filterArea === a.key} color={a.color} onClick={() => setFilterArea(a.key)}>{a.label}</Chip>)}
      </div>

      <div className="space-y-2">
        {filtered.map(t => {
          const assignee = t.assigned_member_id ? members.find(m => m.id === t.assigned_member_id) : null;
          return (
            <div key={t.id} className="bg-white rounded-lg p-3 border border-[#e6d3c0] flex items-start gap-3">
              <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: areaMeta(t.area).color }} />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-[#4a1c17]">{t.title}</div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F7EBDF] text-[#4a1c17]">{FREQ.find(f => f.key === t.frequency)?.label || t.frequency}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F7EBDF] text-[#4a1c17]">{SLOTS.find(s => s.key === t.slot)?.label || t.slot}</span>
                  {t.frequency === "weekly" && (t.days_of_week || []).length > 0 && (
                    <span className="text-[10px] text-neutral-600">{(t.days_of_week || []).map(i => DAYS[i]).join(", ")}</span>
                  )}
                </div>
                {t.description && <div className="text-xs text-neutral-600 mt-0.5">{t.description}</div>}
                <div className="text-xs text-neutral-500 mt-1">
                  {assignee ? `Assegnato a ${assignee.name}` : `Chiunque in ${areaMeta(t.area).label}`}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button data-testid={`staff-task-edit-${t.id}`} onClick={() => setEditing(t)} className="p-1.5 text-neutral-600"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`staff-task-del-${t.id}`} onClick={() => remove(t.id)} className="p-1.5 text-neutral-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && <TaskEditor task={editing} members={members} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function TaskEditor({ task, members, onSave, onCancel }) {
  const [t, setT] = useState({ ...task });
  function toggleDay(i) {
    const d = new Set(t.days_of_week || []);
    d.has(i) ? d.delete(i) : d.add(i);
    setT({ ...t, days_of_week: [...d].sort() });
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#e6d3c0] p-4 flex items-center justify-between">
          <h3 className="font-bold text-[#4a1c17] text-lg">{t.id ? "Modifica attività" : "Nuova attività"}</h3>
          <button onClick={onCancel}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="text-sm block">Titolo
            <input data-testid="task-title-input" value={t.title} onChange={e => setT({ ...t, title: e.target.value })} className="w-full border rounded px-2 py-1.5 mt-1" />
          </label>
          <label className="text-sm block">Descrizione
            <textarea value={t.description || ""} onChange={e => setT({ ...t, description: e.target.value })} rows={2} className="w-full border rounded px-2 py-1.5 mt-1" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">Area
              <select value={t.area} onChange={e => setT({ ...t, area: e.target.value })} className="w-full border rounded px-2 py-1.5 mt-1">
                {AREAS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </label>
            <label className="text-sm">Assegnato a
              <select value={t.assigned_member_id || ""} onChange={e => setT({ ...t, assigned_member_id: e.target.value || null })} className="w-full border rounded px-2 py-1.5 mt-1">
                <option value="">Chiunque nell'area</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
            <label className="text-sm">Frequenza
              <select value={t.frequency} onChange={e => setT({ ...t, frequency: e.target.value })} className="w-full border rounded px-2 py-1.5 mt-1">
                {FREQ.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </label>
            <label className="text-sm">Momento
              <select value={t.slot} onChange={e => setT({ ...t, slot: e.target.value })} className="w-full border rounded px-2 py-1.5 mt-1">
                {SLOTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
          </div>
          {t.frequency === "weekly" && (
            <div>
              <div className="text-sm font-medium mb-1">Giorni</div>
              <div className="flex gap-1 flex-wrap">
                {DAYS.map((d, i) => (
                  <button key={i} onClick={() => toggleDay(i)} className={`px-2 py-1 rounded text-sm ${(t.days_of_week || []).includes(i) ? "bg-[#4a1c17] text-white" : "bg-[#F7EBDF] text-[#4a1c17]"}`}>{d}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#e6d3c0] p-3 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 border rounded-lg text-sm">Annulla</button>
          <button data-testid="staff-save-task" onClick={() => onSave(t)} className="px-3 py-2 bg-[#4a1c17] text-white rounded-lg text-sm flex items-center gap-1">
            <Save className="h-4 w-4" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------- CALENDAR TAB -------------------

function CalendarTab({ members }) {
  const [week, setWeek] = useState(null);
  useEffect(() => {
    (async () => {
      // Reuse the public /staff/week (needs PIN)
      const pinRes = await axios.get(`${API}/admin/staff/pin`, { headers: authHeaders() });
      const pin = pinRes.data.pin;
      const r = await axios.get(`${API}/staff/week`, { headers: { "X-STAFF-PIN": pin } });
      setWeek(r.data);
    })();
  }, [members]);

  if (!week) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-[#4a1c17]">Calendario settimanale</h3>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {week.days.map(d => (
          <div key={d.date} className={`bg-white rounded-lg p-3 border ${d.dow === 1 ? "border-red-300 bg-red-50" : "border-[#e6d3c0]"}`}>
            <div className="flex items-baseline justify-between">
              <div className="font-bold text-[#4a1c17]">{d.day_name}</div>
              <div className="text-xs text-neutral-500">{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</div>
            </div>
            {d.dow === 1 && <div className="text-[10px] text-red-700 font-medium">CHIUSURA</div>}
            <div className="mt-2 space-y-1">
              {d.slots.filter(s => !s.off).map((s, idx) => (
                <div key={idx} className="text-xs p-1.5 rounded flex items-center gap-1" style={{ backgroundColor: (s.color || "#4a1c17") + "22" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="font-medium">{s.name}</span>
                  <span className="ml-auto text-neutral-600">{s.start}-{s.end}</span>
                </div>
              ))}
              {d.slots.filter(s => !s.off).length === 0 && <div className="text-xs text-neutral-500 italic">Nessuno</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------- REPORT TAB -------------------

function ReportTab({ members }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/staff/report/week`, { headers: authHeaders() });
      setReport(r.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (loading || !report) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;

  const totalTasks = report.days.reduce((s, d) => s + d.total, 0);
  const totalDone = report.days.reduce((s, d) => s + d.done, 0);
  const pct = totalTasks ? Math.round(totalDone / totalTasks * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#4a1c17]">Report settimana</h3>
          <p className="text-sm text-neutral-600">Settimana del {report.week_start}</p>
        </div>
        <button onClick={load} className="px-3 py-2 border border-[#4a1c17] text-[#4a1c17] rounded-lg text-sm flex items-center gap-1"><RefreshCw className="h-4 w-4" /> Aggiorna</button>
      </div>

      <div className="bg-[#F7EBDF] rounded-xl p-4">
        <div className="flex items-baseline justify-between">
          <div className="text-[#4a1c17]"><span className="text-3xl font-bold">{totalDone}</span> / {totalTasks}</div>
          <div className="text-2xl font-bold text-[#4a1c17]">{pct}%</div>
        </div>
        <div className="text-sm text-neutral-700 mt-1">Attività completate questa settimana</div>
        <div className="mt-2 bg-white/50 rounded-full h-2 overflow-hidden">
          <div className="h-full bg-[#4a1c17]" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {report.days.map(d => (
          <div key={d.date} className={`bg-white rounded-lg p-3 border ${d.dow === 1 ? "opacity-60" : ""} border-[#e6d3c0]`}>
            <div className="flex items-center justify-between">
              <div className="font-bold text-[#4a1c17]">{d.day_name} <span className="text-xs text-neutral-500 font-normal">{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</span></div>
              <div className="text-sm"><span className="font-bold">{d.done}</span>/{d.total}</div>
            </div>
            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              {d.tasks.map(t => (
                <div key={t.task_id} className="flex items-center gap-2 text-xs">
                  <div className={`h-4 w-4 rounded flex items-center justify-center flex-shrink-0 ${t.done ? "bg-[#4a1c17] text-white" : "border border-neutral-300"}`}>
                    {t.done && <Check className="h-3 w-3" />}
                  </div>
                  <span className={t.done ? "line-through text-neutral-500" : ""}>{t.title}</span>
                  {t.completed_by && <span className="text-neutral-500">· {t.completed_by}</span>}
                </div>
              ))}
              {d.tasks.length === 0 && <div className="text-xs text-neutral-500 italic">Nessuna attività</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------- SETTINGS TAB -------------------

function SettingsTab() {
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const publicUrl = `${window.location.origin}/team`;
  useEffect(() => { (async () => {
    const r = await axios.get(`${API}/admin/staff/pin`, { headers: authHeaders() });
    setPin(r.data.pin);
  })(); }, []);

  async function save() {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/staff/pin`, { pin }, { headers: authHeaders() });
      toast({ title: "PIN aggiornato" });
    } catch (e) { toast({ title: "Errore", description: e.message, variant: "destructive" }); }
    finally { setSaving(false); }
  }

  async function seed() {
    if (!window.confirm("Popola dati di default (team + attività)? Non sovrascrive quelli esistenti.")) return;
    const r = await axios.post(`${API}/admin/staff/seed`, {}, { headers: authHeaders() });
    toast({ title: "Seed completato", description: `+${r.data.members_added} team, +${r.data.tasks_added} attività` });
    window.location.reload();
  }

  return (
    <div className="space-y-4 max-w-lg">
      <h3 className="text-lg font-bold text-[#4a1c17]">Impostazioni Team</h3>

      <div className="bg-white rounded-xl p-4 border border-[#e6d3c0] space-y-3">
        <div className="font-medium text-[#4a1c17]">Accesso PWA Team</div>
        <div className="text-sm text-neutral-600">Il team accede via PIN alla pagina pubblica <code className="bg-[#F7EBDF] px-1 rounded">{publicUrl}</code></div>
        <label className="text-sm block">PIN
          <input data-testid="staff-pin-input" value={pin} onChange={e => setPin(e.target.value)} className="w-full border rounded px-2 py-1.5 mt-1 font-mono" />
        </label>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="staff-pin-save" onClick={save} disabled={saving} className="px-3 py-2 bg-[#4a1c17] text-white rounded-lg text-sm">Salva PIN</button>
          <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast({ title: "Link copiato" }); }} className="px-3 py-2 border border-[#4a1c17] text-[#4a1c17] rounded-lg text-sm flex items-center gap-1"><Copy className="h-4 w-4" /> Copia link</button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 border border-[#e6d3c0] space-y-2">
        <div className="font-medium text-[#4a1c17]">Popolamento iniziale</div>
        <p className="text-sm text-neutral-600">Aggiungi il team e le attività di default (Marco, Elisa, Rodolfo, Alessandro, Claudio, Diva, Noemi, Francesco, Antonio, Governante).</p>
        <button onClick={seed} data-testid="staff-seed-btn" className="px-3 py-2 border border-[#4a1c17] text-[#4a1c17] rounded-lg text-sm">Popola dati di default</button>
      </div>
    </div>
  );
}

// ------------------- MAIN -------------------

export default function StaffPanel() {
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  async function reload() {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/admin/staff/members`, { headers: authHeaders() }),
        axios.get(`${API}/admin/staff/tasks`, { headers: authHeaders() }),
      ]);
      setMembers(m.data);
      setTasks(t.data);
    } catch (e) {
      toast({ title: "Errore caricamento", description: e.message, variant: "destructive" });
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  const tabs = [
    { key: "members", label: "Team", icon: <Users className="h-4 w-4" /> },
    { key: "tasks", label: "Attività", icon: <ClipboardList className="h-4 w-4" /> },
    { key: "calendar", label: "Calendario", icon: <CalendarDays className="h-4 w-4" /> },
    { key: "report", label: "Report", icon: <BarChart3 className="h-4 w-4" /> },
    { key: "settings", label: "Impostazioni", icon: null },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-1 overflow-x-auto pb-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`staff-tab-${t.key}`} className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1 whitespace-nowrap ${tab === t.key ? "bg-[#4a1c17] text-white" : "bg-white text-[#4a1c17] border border-[#e6d3c0]"}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {loading && tab !== "settings" ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>
      ) : tab === "members" ? (
        <MembersTab members={members} tasks={tasks} onReload={reload} />
      ) : tab === "tasks" ? (
        <TasksTab tasks={tasks} members={members} onReload={reload} />
      ) : tab === "calendar" ? (
        <CalendarTab members={members} />
      ) : tab === "report" ? (
        <ReportTab members={members} />
      ) : (
        <SettingsTab />
      )}
    </div>
  );
}
